export const SDK_VERSION = '0.1.2';
export const DEFAULT_API_BASE_URL = 'https://api.pillow.so';
export const HEARTBEAT_INTERVAL_MS = 60000;
export const DEFAULT_BACKDROP_OPTIONS = {
    blur: 6,
    color: '#0f172a',
    alpha: 0.42,
};
export const DEFAULT_PRESENTATION_OPTIONS = {
    mode: 'modal',
    position: 'bottom-right',
    zIndex: 999999,
    scale: 1,
};
export const noopLogger = {
    debug() { },
    info() { },
    warn() { },
    error() { },
};
export function mergePresentationOptions(base, override) {
    const merged = {
        ...DEFAULT_PRESENTATION_OPTIONS,
        ...base,
        ...override,
    };
    return {
        ...merged,
        backdrop: {
            ...DEFAULT_BACKDROP_OPTIONS,
            ...base?.backdrop,
            ...override?.backdrop,
        },
        mode: resolvePresentationMode(merged),
    };
}
export function resolvePresentationMode(options) {
    return options.mode ?? 'modal';
}
export function normalizeBaseUrl(baseUrl) {
    const trimmed = (baseUrl ?? DEFAULT_API_BASE_URL).trim();
    const normalized = trimmed.replace(/\/+$/, '');
    try {
        const url = new URL(normalized);
        if (url.hostname === '0.0.0.0') {
            url.hostname = 'localhost';
            return url.toString().replace(/\/+$/, '');
        }
    }
    catch {
        return normalized;
    }
    return normalized;
}
export function normalizeError(error, fallback) {
    if (error instanceof Error) {
        return error;
    }
    return new Error(fallback);
}
export function assertNonEmpty(value, field) {
    const trimmed = value.trim();
    if (!trimmed) {
        throw new Error(`${field} must not be blank`);
    }
    return trimmed;
}
export function shortHash(value) {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
        hash = ((hash << 5) - hash + value.charCodeAt(i)) | 0;
    }
    return (hash >>> 0).toString(36);
}
export function createUuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    const bytes = new Uint8Array(16);
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
        crypto.getRandomValues(bytes);
    }
    else {
        for (let index = 0; index < bytes.length; index += 1) {
            bytes[index] = Math.floor(Math.random() * 256);
        }
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
    return [
        hex.slice(0, 8),
        hex.slice(8, 12),
        hex.slice(12, 16),
        hex.slice(16, 20),
        hex.slice(20),
    ].join('-');
}
export function readNavigatorMetadata() {
    const timezone = (() => {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
        }
        catch {
            return '';
        }
    })();
    return compactStringRecord({
        app_name: typeof document !== 'undefined' ? document.title || undefined : undefined,
        os_name: typeof navigator !== 'undefined' ? navigator.platform || undefined : undefined,
        device_model: typeof navigator !== 'undefined' ? navigator.userAgent || undefined : undefined,
        locale: typeof navigator !== 'undefined' ? navigator.language || undefined : undefined,
        timezone: timezone || undefined,
    });
}
export function compactRecord(value) {
    const next = {};
    for (const [key, entry] of Object.entries(value)) {
        if (entry !== null && entry !== undefined) {
            next[key] = entry;
        }
    }
    return next;
}
export function compactStringRecord(value) {
    const next = {};
    for (const [key, entry] of Object.entries(value)) {
        if (typeof entry === 'string') {
            next[key] = entry;
        }
    }
    return next;
}
export function validateColor(value, fallback) {
    if (!value)
        return fallback;
    if (/^#[0-9A-Fa-f]{3,8}$/.test(value))
        return value;
    if (/^(rgb|hsl)a?\([^)]+\)$/.test(value))
        return value;
    if (/^[a-zA-Z]+$/.test(value))
        return value;
    return fallback;
}
export function validateDimension(value, fallback) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0)
        return `${value}px`;
    if (typeof value === 'string' && /^\d+(\.\d+)?(px|em|rem|vh|vw|%)$/.test(value))
        return value;
    return fallback;
}
export function validateZIndex(value, fallback) {
    if (typeof value === 'number' && Number.isInteger(value) && value > 0)
        return value;
    return fallback;
}
export function validateBlur(value, fallback) {
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0)
        return value;
    return fallback;
}
export function validateAlpha(value, fallback) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return fallback;
    }
    if (value < 0)
        return 0;
    if (value > 1)
        return 1;
    return value;
}
export function sanitizePrimitiveProperties(properties) {
    const next = {};
    for (const [key, value] of Object.entries(properties)) {
        if (!key.trim()) {
            continue;
        }
        if (typeof value === 'string' ||
            typeof value === 'boolean' ||
            typeof value === 'number') {
            next[key.trim()] = value;
        }
    }
    return next;
}
//# sourceMappingURL=shared.js.map